import React from "react";
import "react-toggle/style.css"
import 'react-input-checkbox/lib/react-input-checkbox.min.css';
import Select from 'react-select'
import { Card, ListGroup, OverlayTrigger, Alert, InputGroup } from 'react-bootstrap'
import { connect } from "react-redux";
import { renderCoverageTooltip } from "../components/CoverageDescriptions";
import ButtonContinue from "../components/ButtonContinue";
import { IQuoteStepState, IQuoteStepComponentProps } from "../components/interfaces";
import QuoteStep from "./QuoteStepLayout";
import { QUOTE_STEPS, COMPONENTS } from "../AppConstants";
import { ICoverageConfig, IListOption, ICoverage, IQuoteApiClient, IPaths } from "../classes/Domain";
import QuoteStepBase, { mapStepPropsBase } from '../components/QuoteStepBase'
import ReactLoading from 'react-loading';
import Utilities from "../classes/Utilities";
import * as _ from 'lodash';
import SwipeableViews from 'react-swipeable-views';
import QuoteActions from "../state/appActions";
import { IAppState } from "../state/interfaces";


const rejectOption = { value: "REJECT", label: "I Reject" };

interface IAppStateUpdate {
    coverageConfig: ICoverageConfig;
}

interface IState extends IQuoteStepState {
    coverageConfigs: Array<ICoverageConfig>,  
    selectedLimit: IListOption | null,
    loadingConfigs: boolean;
}

export interface IProps extends IQuoteStepComponentProps {
    quoteApiClient: IQuoteApiClient,
    PATHS: IPaths,
    rateError: string | null;
}

interface ICoverageMap {
    coverageConfigCode: string,
    code: string,
    name: string,
    canBeDeclined: boolean,
    Ineligible: boolean,
    limits: Array<IListOption>
}

class Coverage extends QuoteStepBase<IProps, IState> {
    constructor(props: IProps) {
        super(props);
        this.next = this.next.bind(this);
        this.getSelectedLimit = this.getSelectedLimit.bind(this);
        this.onLimitChange = this.onLimitChange.bind(this);
        this.rejectCorrelationCoverage = this.rejectCorrelationCoverage.bind(this);
        this.selectCorrelationCoverage = this.selectCorrelationCoverage.bind(this);

        this.state = {
            loadingConfigs: true,
            coverageConfigs: [],
            updatingStore: false,
            selectedLimit: null,
            errors: null
        }
    }

    componentDidMount() {
        super.componentDidMount();
        this.props.reportCurrentStep();

        const coveragesApiClient = this.props.quoteApiClient;

        this.setState({ loadingConfigs: true }, () => {
            coveragesApiClient
                .getCoverageConfigs(this.props.quoteUid)
                .then((response) => {
                    this.setState({ ...this.state, loadingConfigs: false, coverageConfigs : response.data });
                });
        });
    }

    next(selectedCoverageConfig : ICoverageConfig) {
        this.setState({ updatingStore: true }, () => {
            this.props.updateAppState({ coverageConfig: selectedCoverageConfig })
                .then(() => this.props.history.push(this.props.PATHS.RESULT),
                    () => {
                        this.setState({ ...this.state, updatingStore: false });
                    });
        });
    }

    mapCoverages(coverageConfigCode: string, coverages: Array<ICoverage>): Array<ICoverageMap> {
        const coverageGroups = _.groupBy(coverages, "code");
        return _.map(coverageGroups, (group, coverageCode) => {
            return {
                coverageConfigCode,
                code: coverageCode,
                name: group[0].name,
                canBeDeclined: !!_.find(group, (c) => c.canBeDeclined),
                Ineligible: !!_.find(group, (c) => c.Ineligible),
                limits: _.map(_.sortBy(group, ["order"]), (c) => ({ label: c.limit, value: c.value }))
            };
        });
    }

    findCoveragesIn(config: ICoverageConfig, coverageCode: string, vehicleId: number | null = null) : Array<ICoverage> {
        if (null == vehicleId) {
            return _.filter(config.liabilityCoverages, (cov) => cov.code === coverageCode);
        }

        const vehicleCoverages = _.find(config.vehicleCoverages, (vcov) => vcov.vehicleId === vehicleId);
        if (!vehicleCoverages) return [];
        return _.filter(vehicleCoverages.coverages, (cov) => cov.code === coverageCode);
    }

    getSelectedLimit(coverageConfigCode: string, coverageCode: string, vehicleId: number | null = null): IListOption | null {
        const coverageConfig = _.find(this.state.coverageConfigs, (config) => config.code === coverageConfigCode);
        if (!coverageConfig) return null;

        const coverage = _.find(this.findCoveragesIn(coverageConfig, coverageCode, vehicleId), (cov) => cov.isSelected);
        return !coverage ? null : { label: coverage.limit, value: coverage.value };
    }

    getFirstLimit(coverageConfigCode: string, coverageCode: string, vehicleId: number | null = null): IListOption | null {
        const coverageConfig = _.find(this.state.coverageConfigs, (config) => config.code === coverageConfigCode);
        if (!coverageConfig) return null;

        const coverage = _.find(this.findCoveragesIn(coverageConfig, coverageCode, vehicleId), (cov) => cov.order == 0);
        return !coverage ? null : { label: coverage.limit, value: coverage.value };
    }

    selectCorrelationCoverage(coverageConfig: ICoverageConfig, coverageCode: string, vehicleId: number | null = null, limit: IListOption | null = null)
    {
        const coverages = this.findCoveragesIn(coverageConfig, coverageCode, vehicleId);
        const cov = limit
            ? _.find(coverages, (cov) => cov.value === limit.value)
            : _.find(coverages, (cov) => cov.value !== rejectOption.value);

        for (var i = 0; i < coverages.length; i++) {
            coverages[i].isSelected = false;
        }

        if (cov)
        {
            cov.isSelected = true;
        }
    }

    rejectCorrelationCoverage(coverageConfig: ICoverageConfig, coverageCode: string, vehicleId: number | null = null)
    {
        const coverages = this.findCoveragesIn(coverageConfig, coverageCode, vehicleId);
        const cov = _.find(coverages, (cov) => cov.value === rejectOption.value);

        for (var i = 0; i < coverages.length; i++) {
            coverages[i].isSelected = false;
        }

        if (cov)
        {
            cov.isSelected = true;
        }
    }

    onLimitChange(limit: IListOption | null, coverageConfigCode: string, coverageCode: string, vehicleId: number | null = null)
    {
        const { coverageConfigs } = { ...this.state };

        const coverageConfig = _.find(this.state.coverageConfigs, (config) => config.code === coverageConfigCode);
        if (!coverageConfig) return;

        const coverages = this.findCoveragesIn(coverageConfig, coverageCode, vehicleId);

        for (var i = 0; i < coverages.length; i++) {
            coverages[i].isSelected = false;

            if (limit && limit.value === rejectOption.value)
            {
                switch (coverages[i].code) {
                    case "UM":
                        {
                            this.rejectCorrelationCoverage(coverageConfig, "UMPD", vehicleId);
                            break;
                        }
                    case "COMP":
                        {
                            this.rejectCorrelationCoverage(coverageConfig, "COLL", vehicleId);
                            this.rejectCorrelationCoverage(coverageConfig, "TL", vehicleId);
                            break;
                        }
                    case "COLL":
                        {
                            this.rejectCorrelationCoverage(coverageConfig, "COMP", vehicleId);
                            this.rejectCorrelationCoverage(coverageConfig, "TL", vehicleId);
                            break;
                        }
                }
            }
        }

        if (limit && limit.value !== rejectOption.value) {
            const coverage = _.find(coverages, (cov) => cov.value === limit?.value);
            
            if (coverage)
            {
                switch (coverage.code)
                {
                    case "UM":
                        {
                            let biLimit = this.getSelectedLimit(coverageConfig.code, "BI", null);

                            if (biLimit)
                            {
                                var _umLimit = limit.value.split("/")[0];
                                var _biLimit = biLimit.value.split("/")[0];

                                if (_umLimit && _biLimit && Number(_biLimit) < Number(_umLimit))
                                {
                                    this.selectCorrelationCoverage(coverageConfig, "BI", vehicleId, limit);
                                }
                            }

                            break;
                        }
                    case "BI":
                        {
                            let umLimit = this.getSelectedLimit(coverageConfig.code, "UM", null);

                            if (umLimit && umLimit.value !== rejectOption.value)
                            {
                                var _biLimit = limit.value.split("/")[0];
                                var _umLimit = umLimit.value.split("/")[0];

                                if (_biLimit && _umLimit && Number(_umLimit) > Number(_biLimit))
                                {
                                    this.selectCorrelationCoverage(coverageConfig, "UM", vehicleId, limit);
                                }
                            }

                            break;
                        }
                    case "PIP":
                        {

                            this.selectCorrelationCoverage(coverageConfig, "MED", vehicleId, rejectOption);
                            break;
                        }
                    case "MED":
                        {
                            this.selectCorrelationCoverage(coverageConfig, "PIP", vehicleId, rejectOption);
                            break;
                        }
                    case "UMPD":
                        {
                            let umLimit = this.getSelectedLimit(coverageConfig.code, "UM", null);

                            this.selectCorrelationCoverage(coverageConfig, "UM", vehicleId, umLimit);
                            break;
                        }
                    case "COMP":
                        {
                            this.selectCorrelationCoverage(coverageConfig, "COLL", vehicleId, limit);
                            break;
                        }
                    case "COLL":
                        {
                            this.selectCorrelationCoverage(coverageConfig, "COMP", vehicleId, limit);
                            break;
                        }
                    case "TL":
                        {
                            for (var i = 0; i < coverageConfig.vehicleCoverages.length; i++)
                            {
                                if (coverageConfig.vehicleCoverages[i].vehicleId != vehicleId)
                                {
                                    this.selectCorrelationCoverage(coverageConfig, "TL", coverageConfig.vehicleCoverages[i].vehicleId, limit);
                                }

                                let compLimit = this.getSelectedLimit(coverageConfig.code, "COMP", coverageConfig.vehicleCoverages[i].vehicleId);

                                this.selectCorrelationCoverage(coverageConfig, "COMP", coverageConfig.vehicleCoverages[i].vehicleId, compLimit);
                                this.selectCorrelationCoverage(coverageConfig, "COLL", coverageConfig.vehicleCoverages[i].vehicleId, compLimit);
                            }
                            break;
                        }
                    case "RREIM":
                        {
                            for (var i = 0; i < coverageConfig.vehicleCoverages.length; i++)
                            {
                                if (coverageConfig.vehicleCoverages[i].vehicleId != vehicleId)
                                {
                                    this.selectCorrelationCoverage(coverageConfig, "RREIM", coverageConfig.vehicleCoverages[i].vehicleId, limit);
                                }
                            }
                            break;
                        }
                }

                coverage.isSelected = true;
            }
        }

        this.setState({...this.state, coverageConfigs});
    }

    renderCoverage(coverageMap: ICoverageMap, vehicleId: number | null = null)
    {
        return (1 === coverageMap.limits.length && !coverageMap.canBeDeclined) || coverageMap.Ineligible
            ? 
            <ListGroup.Item key={Utilities.uniqueId()}>
                    <label className="left-cell">
                    <span>
                        {coverageMap.name}&nbsp;
                        <OverlayTrigger
                            trigger={["hover", "click"]}
                            placement="bottom"
                            delay={{ show: 250, hide: 400 }}
                            overlay={(props) => renderCoverageTooltip(coverageMap.code, props, this.props.stateCode)}>
                            <i className="fas fa-info-circle"></i>
                        </OverlayTrigger>
                    </span>
                    </label>
                    <label className="right-cell">
                        {coverageMap.limits[0].label}
                    </label>
                </ListGroup.Item>
            :
            <ListGroup.Item key={Utilities.uniqueId()}>
                <label className="left-cell">
                <span>
                    {coverageMap.name}&nbsp;
                        <OverlayTrigger
                        trigger={["hover", "click"]}
                        placement="bottom"
                        delay={{ show: 250, hide: 400 }}
                        overlay={(props) => renderCoverageTooltip(coverageMap.code, props, this.props.stateCode)}>
                        <i className="fas fa-info-circle"></i>
                    </OverlayTrigger>
                </span>
                </label>
                <label className="right-cell">
                    <Select
                        key={Utilities.uniqueId()}
                        options={coverageMap.canBeDeclined ? [rejectOption, ...coverageMap.limits] : coverageMap.limits}
                        value={coverageMap.canBeDeclined ? this.getSelectedLimit(coverageMap.coverageConfigCode, coverageMap.code, vehicleId) ?? rejectOption : this.getSelectedLimit(coverageMap.coverageConfigCode, coverageMap.code, vehicleId)}
                        onChange={(v) => this.onLimitChange(v ? { value: v["value"], label: v["label"] } : null, coverageMap.coverageConfigCode, coverageMap.code, vehicleId)}
                        isClearable={false} />
                </label>
            </ListGroup.Item>
    }

    render() {
        const coverageConfigSlides = this.state.coverageConfigs.map((config) =>
            <Card key={Utilities.uniqueId()} className="coverages-card">
                <Card.Body key={Utilities.uniqueId()}>
                    <Card.Title key={Utilities.uniqueId()}>
                        {config.name}
                    </Card.Title>

                    <ListGroup variant="flush">
                        {
                            this.mapCoverages(config.code, config.liabilityCoverages).map((coverageMap) => this.renderCoverage(coverageMap))
                        }
                        {config.vehicleCoverages.map((vehicleCoverages) =>
                            <div key={Utilities.uniqueId()}>
                                <ListGroup.Item key={Utilities.uniqueId()} variant="primary">{vehicleCoverages.vehicleDescription}</ListGroup.Item>
                                {
                                    this.mapCoverages(config.code, vehicleCoverages.coverages).map((coverageMap) => this.renderCoverage(coverageMap, vehicleCoverages.vehicleId))
                                }
                            </div>
                        )}
                    </ListGroup>
                    <div className="btnContainer">
                        <ButtonContinue
                            label="Select"
                            disabled={this.state.updatingStore}
                            showLoading={this.state.updatingStore}
                            onClick={() => this.next(config)} />
                    </div>
                </Card.Body>
            </Card>
        );
        return (
            <QuoteStep id={this.props.id}>
                <div className="quote-step coverages">
                    {this.state.loadingConfigs
                        ? <div className={"w-100 loading-indicator"}><ReactLoading type={"spin"} color={"#007bff"} height={'40px'} width={'40px'} /></div>
                        : null
                    }

                    {
                        this.props.rateError
                            ?
                            <div>
                                <Alert variant={"warning"}>
                                    <h5 className="text-center">
                                        <strong className="text-uppercase">Sorry we can't continue</strong>
                                        <br />
                                        {this.props.rateError}
                                    </h5>
                                </Alert>
                            </div>
                            :
                            null
                    }

                    {!this.state.loadingConfigs
                        ? <SwipeableViews enableMouseEvents>
                            {coverageConfigSlides}
                        </SwipeableViews>
                        : null
                    }
                </div>
            </QuoteStep>
        );
    }
}

let mapProps = (state: IAppState) => {
    return {
        quoteUid: state.quoteUid,
        meta: { propsSource: COMPONENTS.PROPS_SOURCES.APP_STORE },
        rateError: state.rateError,
        stateCode: state.stateCode
    };
};

let mapDispatch = (dispatch: any, ownProps: IProps) => {
    var quoteActions = new QuoteActions(ownProps.quoteApiClient);

    return {
        reportCurrentStep: () => dispatch(quoteActions.quoteStepChanged(QUOTE_STEPS.COVERAGES)),
        updateAppState: (update: IAppStateUpdate) => dispatch(quoteActions.selectCoverageConfig(update.coverageConfig))
    };
}

export default connect(mapProps, mapDispatch)(Coverage);
